<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue';
import { uniqueId } from 'lodash-es';
import { IdPrefix } from '@/modules/common/enums/id-prefix.ts';
import Validator from '@/modules/common/utils/validator.ts';
import { ValueUtil } from '@/modules/common/utils/value.util.ts';
import { useFormTooltips } from '@/modules/ui/composables/use-form-tooltips.ts';
import {
  COUNTERPARTY_NAME_DUPLICATE_ERROR,
  useCounterpartyNameDuplicate,
} from '@/modules/counterparty/counterparty-master/composables/use-counterparty-duplicate.ts';
import CounterpartyNameMatchIndicator from '@/modules/counterparty/counterparty-master/components/CounterpartyNameMatchIndicator.vue';
import UiField from '@/modules/ui/components/ui/UiField.vue';
import UiTextarea from '@/modules/ui/components/ui/UiTextarea.vue';

const NAME_PLACEHOLDER = 'counterparty.master.steps.name.field.name';

const name = defineModel<string>({ required: true });
const valid = defineModel<boolean>('valid', { default: false });

const { showErrors = false } = defineProps<{
  showErrors?: boolean;
}>();

const { tooltipsDisabled, onInput } = useFormTooltips();
const fieldId = uniqueId(IdPrefix.Control);
const touched = ref(false);
const focused = ref(false);
const { isDuplicate, isChecked, isLoading, similarMatches, check } = useCounterpartyNameDuplicate(name, {
  showErrors: () => showErrors,
});

const requiredError = computed(() => Validator.required(name.value.trim()) ?? null);

const error = computed(() => {
  if (isDuplicate.value && isChecked.value) {
    return COUNTERPARTY_NAME_DUPLICATE_ERROR;
  }

  if (!(touched.value || showErrors)) {
    return null;
  }

  return requiredError.value;
});

watch(
  [requiredError, isDuplicate, isChecked],
  () => {
    valid.value = !requiredError.value && !isDuplicate.value && isChecked.value;
  },
  { immediate: true }
);

watch(
  name,
  async (value, prev) => {
    if (prev !== undefined && value.trim() === prev.trim()) {
      return;
    }

    touched.value = false;

    if (!value.trim() || focused.value) {
      return;
    }

    await nextTick();

    if (focused.value) {
      return;
    }

    touched.value = true;
    await check();
  },
  { immediate: true }
);

function onFocus() {
  focused.value = true;
}

async function onBlur() {
  focused.value = false;
  touched.value = true;
  await check();
}
</script>

<template>
  <div
    class="name-field"
    :class="{ '_with-indicator': isLoading || similarMatches.length }"
  >
    <UiField
      :id="fieldId"
      stretch
      :error="error"
      with-tooltip
      :tooltip="ValueUtil.placeholderTooltip(NAME_PLACEHOLDER, name)"
      :tooltip-disabled="tooltipsDisabled"
    >
      <UiTextarea
        :id="fieldId"
        v-model="name"
        :placeholder="NAME_PLACEHOLDER"
        :error="!!error"
        :max-length="255"
        :rows="3"
        no-resize
        @focus="onFocus"
        @blur="onBlur"
        @update:model-value="onInput"
      />
    </UiField>

    <CounterpartyNameMatchIndicator
      :loading="isLoading"
      :matches="similarMatches"
    />
  </div>
</template>

<style scoped lang="scss">
.name-field {
  position: relative;

  &._with-indicator {
    :deep(.ui-textarea) {
      padding-right: 32px;
    }
  }
}
</style>
